import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ISelectType, useFormContext } from "@4uhub/lib4uhub";
import { useFieldArray } from "react-hook-form";

export interface ICustomButton {
  id: string;
  order: number;
  text: string;
}

export interface ICtaButton {
  id: string;
  order: number;
  actionType: ISelectType;
  text: string;
  payload: string;
}

export interface ITemplateWhatsBasicData {
  title: string;
  body: string;
  footer: string;
}

export type TButtonsList = "1" | "2";

interface ITemplateWhatsContextProps {
  customButtons: ICustomButton[];
  ctaButtons: ICtaButton[];
  buttonsList: TButtonsList[];
  actionTypes: ISelectType[];
  templateWhatsBasicData: ITemplateWhatsBasicData;
  showAllOptions: boolean;
  isFixedData: boolean;
  variables: string[];
  software: "ChatBot" | "MarCom";
  addCtaHandler: (ctaButton: ICtaButton) => void;
  updateCtaHandler: (id: string, fieldName: string, value: string) => void;
  deleteCtaHandler: (id: string, index: number) => void;
  reorderCtaButtons: (list: ICtaButton[]) => void;
  addCustomButtonHandler: (customButton: ICustomButton) => void;
  updateCustomButtonHandler: (
    id: string,
    fieldName: string,
    value: string,
  ) => void;
  deleteCustomButtonHandler: (id: string) => void;
  reorderCustomButtons: (list: ICustomButton[]) => void;
  setCustomButtonsArray: (value: ICustomButton[]) => void;
  updateBasicData: (fieldName: string, value: string) => void;
  toggleButtonsList: (item: TButtonsList) => void;
  openAllOptions: () => void;
  closeAllOptions: () => void;
  fixDataHandler: (value: boolean) => void;
  fetchActionTypes: (value: ISelectType[]) => void;
  setFixedCustomButton: (value: ICustomButton, index: number) => void;
  removeFixedCustomButton: (index: number) => void;
  fetchVariables: (values: string[]) => void;
  setFixedCtaButton: (value: ICtaButton, index: number) => void;
}

const TemplateWhatsContext = createContext<
  ITemplateWhatsContextProps | undefined
>(undefined);

interface ITemplateWhatsProviderProps {
  defaultCtaButtons?: ICtaButton[];
  defaultCustomButtons?: ICustomButton[];
  software: "ChatBot" | "MarCom";
  defaultbody: string;
  buttonsOrder: TButtonsList[];
  children: React.ReactNode;
  onGetButtonsOrder: (list: TButtonsList[]) => void;
}

function areButtonsListsEqual(
  currentInternalList: TButtonsList[],
  nextExternalList: TButtonsList[],
) {
  if (currentInternalList.length !== nextExternalList.length) return false;

  for (let position = 0; position < currentInternalList.length; position++) {
    const currentValue = currentInternalList[position];
    const nextValue = nextExternalList[position];
    if (currentValue !== nextValue) return false;
  }

  return true;
}

function getNormalizedButtonsOrderFromProps(
  externalButtonsOrder: TButtonsList[] | undefined,
): TButtonsList[] {
  const defaultOrder: TButtonsList[] = ["1", "2"];
  const hasExternalOrder =
    externalButtonsOrder && externalButtonsOrder.length > 0;

  return hasExternalOrder ? externalButtonsOrder! : defaultOrder;
}

export const TemplateWhatsProvider: React.FC<ITemplateWhatsProviderProps> =
  memo((props) => {
    const {
      defaultCtaButtons,
      defaultCustomButtons,
      buttonsOrder,
      defaultbody,
      children,
      software,
      onGetButtonsOrder,
    } = props;

    const { control, setValue } = useFormContext();

    const {
      append: appendCta,
      remove: removeCta,
      replace: replaceCta,
    } = useFieldArray({ name: "ctaButtons", control });

    const {
      append: appendCustom,
      remove: removeCustom,
      replace: replaceCustom,
    } = useFieldArray({ name: "customButtons", control });

    const [customButtons, setCustomButtons] = useState<ICustomButton[]>(
      defaultCustomButtons || [],
    );
    const [ctaButtons, setCtaButtons] = useState<ICtaButton[]>(
      defaultCtaButtons
        ? defaultCtaButtons.filter((btn) => btn.actionType.id !== "0")
        : [],
    );

    const [buttonsList, setButtonsList] = useState<TButtonsList[]>(
      getNormalizedButtonsOrderFromProps(buttonsOrder),
    );

    const [showAllOptions, setShowAllOptions] = useState(false);
    const [isFixedData, setIsFixedData] = useState(true);
    const [variables, setVariables] = useState<string[]>([]);
    const [actionTypes, setActionTypes] = useState<ISelectType[]>([]);

    const [templateWhatsBasicData, setTemplateWhatsBasicData] =
      useState<ITemplateWhatsBasicData>({
        title: "",
        body: defaultbody,
        footer: "",
      });

    useEffect(() => {
      const nextCtaButtonsFromProps = (defaultCtaButtons || []).filter(
        (ctaButton) => ctaButton.actionType.id !== "0",
      );
      setCtaButtons(nextCtaButtonsFromProps);
      replaceCta(nextCtaButtonsFromProps as any);
    }, [defaultCtaButtons, replaceCta]);

    useEffect(() => {
      const next = defaultCustomButtons || [];
      setCustomButtons(next);
      replaceCustom(next as any);
    }, [defaultCustomButtons, replaceCustom]);

    useEffect(() => {
      setTemplateWhatsBasicData((p) => ({ ...p, body: defaultbody || "" }));
    }, [defaultbody]);

    const notifyParentButtonsOrderRef = useRef(onGetButtonsOrder);

    useEffect(() => {
      notifyParentButtonsOrderRef.current = onGetButtonsOrder;
    }, [onGetButtonsOrder]);

    useEffect(() => {
      const nextButtonsListFromParent =
        getNormalizedButtonsOrderFromProps(buttonsOrder);

      setButtonsList((currentButtonsListInternal) => {
        const isSameContent = areButtonsListsEqual(
          currentButtonsListInternal,
          nextButtonsListFromParent,
        );

        if (isSameContent) return currentButtonsListInternal;

        return nextButtonsListFromParent;
      });
    }, [buttonsOrder]);

    const lastButtonsListSentToParentRef = useRef<string>("");

    useEffect(() => {
      const buttonsListSignature = buttonsList.join("|");
      const hasAlreadySentThisExactOrder =
        lastButtonsListSentToParentRef.current === buttonsListSignature;

      if (hasAlreadySentThisExactOrder) return;

      lastButtonsListSentToParentRef.current = buttonsListSignature;
      notifyParentButtonsOrderRef.current(buttonsList);
    }, [buttonsList]);

    const fetchVariables = useCallback(
      (values: string[]) => setVariables(values),
      [],
    );

    const fetchActionTypes = useCallback(
      (values: ISelectType[]) => setActionTypes(values),
      [],
    );

    const fixDataHandler = useCallback(
      (value: boolean) => setIsFixedData(value),
      [],
    );

    const openAllOptions = useCallback(() => setShowAllOptions(true), []);

    const closeAllOptions = useCallback(() => setShowAllOptions(false), []);

    const toggleButtonsList = useCallback((clickedItem: TButtonsList) => {
      setButtonsList((currentButtonsList) => {
        const clickedIndex = currentButtonsList.indexOf(clickedItem);
        const clickedIsInFirstTwoPositions =
          clickedIndex === 0 || clickedIndex === 1;

        if (!clickedIsInFirstTwoPositions) return currentButtonsList;

        const nextButtonsList = [...currentButtonsList];
        [nextButtonsList[0], nextButtonsList[1]] = [
          nextButtonsList[1],
          nextButtonsList[0],
        ];
        return nextButtonsList;
      });
    }, []);

    const updateBasicData = useCallback((fieldName: string, value: string) => {
      setTemplateWhatsBasicData((previousBasicData) => ({
        ...previousBasicData,
        [fieldName]: value,
      }));
    }, []);

    const addCustomButtonHandler = useCallback(
      (customButton: ICustomButton) => {
        setCustomButtons((previousButtons) => [
          ...previousButtons,
          customButton,
        ]);
        appendCustom(customButton);
      },
      [appendCustom],
    );

    const updateCustomButtonHandler = useCallback(
      (id: string, fieldName: string, value: string) => {
        setCustomButtons((previousButtons) =>
          previousButtons.map((button) =>
            button.id === id ? { ...button, [fieldName]: value } : button,
          ),
        );
      },
      [],
    );

    const deleteCustomButtonHandler = useCallback(
      (id: string) => {
        setCustomButtons((previousButtons) => {
          const customButtonIndex = previousButtons.findIndex(
            (button) => button.id === id,
          );
          if (customButtonIndex > -1) removeCustom(customButtonIndex);
          return previousButtons.filter((button) => button.id !== id);
        });
      },
      [removeCustom],
    );

    const reorderCustomButtons = useCallback(
      (list: ICustomButton[]) => setCustomButtons(list),
      [],
    );

    const addCtaHandler = useCallback(
      (ctaButton: ICtaButton) => {
        setCtaButtons((prev) => [...prev, ctaButton]);
        appendCta(ctaButton);
      },
      [appendCta],
    );

    const updateCtaHandler = useCallback(
      (id: string, fieldName: string, value: string) => {
        setCtaButtons((prev) =>
          prev.map((b) => (b.id === id ? { ...b, [fieldName]: value } : b)),
        );
      },
      [],
    );

    const deleteCtaHandler = useCallback(
      (id: string, index: number) => {
        setCtaButtons((prev) => prev.filter((b) => b.id !== id));
        removeCta(index);
      },
      [removeCta],
    );

    const reorderCtaButtons = useCallback(
      (list: ICtaButton[]) => setCtaButtons(list),
      [],
    );

    const setFixedCtaButton = useCallback(
      (value: ICtaButton, index: number) => {
        if (index >= ctaButtons.length) {
          appendCta(value);
          setCtaButtons((p) => [...p, value]);
        } else {
          setValue(`ctaButtons.${index}`, value);
          setCtaButtons((p) => p.map((b, i) => (i === index ? value : b)));
        }
      },
      [appendCta, ctaButtons.length, setValue],
    );

    const setCustomButtonsArray = useCallback(
      (buttons: ICustomButton[]) => {
        setCustomButtons([...buttons]);
        replaceCustom(buttons as any);
      },
      [replaceCustom],
    );

    const setFixedCustomButton = useCallback(
      (value: ICustomButton, index: number) => {
        if (index >= customButtons.length) {
          appendCustom(value);
          setCustomButtons((p) => [...p, value]);
        } else {
          setValue(`customButtons.${index}`, value);
          setCustomButtons((p) => p.map((b, i) => (i === index ? value : b)));
        }
      },
      [appendCustom, customButtons.length, setValue],
    );

    const removeFixedCustomButton = useCallback(
      (index: number) => {
        if (!customButtons || customButtons.length === 0) return;
        removeCustom(index);
        setCustomButtons((p) => p.filter((_, i) => i !== index));
      },
      [removeCustom, customButtons],
    );

    const value = useMemo(
      () => ({
        customButtons,
        ctaButtons,
        buttonsList,
        templateWhatsBasicData,
        showAllOptions,
        isFixedData,
        variables,
        actionTypes,
        software,
        addCtaHandler,
        updateCtaHandler,
        deleteCtaHandler,
        reorderCtaButtons,
        addCustomButtonHandler,
        updateCustomButtonHandler,
        deleteCustomButtonHandler,
        reorderCustomButtons,
        updateBasicData,
        toggleButtonsList,
        openAllOptions,
        closeAllOptions,
        fixDataHandler,
        setCustomButtonsArray,
        setFixedCustomButton,
        setFixedCtaButton,
        removeFixedCustomButton,
        fetchVariables,
        fetchActionTypes,
      }),
      [
        customButtons,
        ctaButtons,
        buttonsList,
        templateWhatsBasicData,
        showAllOptions,
        isFixedData,
        variables,
        actionTypes,
        software,
        addCtaHandler,
        updateCtaHandler,
        deleteCtaHandler,
        reorderCtaButtons,
        addCustomButtonHandler,
        updateCustomButtonHandler,
        deleteCustomButtonHandler,
        reorderCustomButtons,
        updateBasicData,
        toggleButtonsList,
        openAllOptions,
        closeAllOptions,
        fixDataHandler,
        setCustomButtonsArray,
        setFixedCustomButton,
        setFixedCtaButton,
        removeFixedCustomButton,
        fetchVariables,
        fetchActionTypes,
      ],
    );

    return (
      <TemplateWhatsContext.Provider value={value}>
        {children}
      </TemplateWhatsContext.Provider>
    );
  });

export const useTemplateWhats = () => {
  const context = useContext(TemplateWhatsContext);

  if (!context) {
    throw new Error("useTemplateWhats must be used with TemplateWhatsProvider");
  }

  return context;
};
